import { GenericMap } from './types';
import { IMUItem } from '../types/brandMenu';
import {
  IAISuggestion,
  IUpsellSuggestionDetails,
  SuggestionVerdict,
} from '../redux/features/aiSuggestions/aiSuggestions.props';
import { TopLevelMenuItem } from './menu';

/**
 * HITL-1429 — classifying and resolving the AI's upsell suggestions.
 *
 * The AI fires an upsell from a brand-menu constraint nested under one of its
 * own internal container items (`post_checkread_back_upsell`,
 * `post_item_complete`, `end_of_order`). Agents cannot reach those items in the
 * HITL client — they complete the same upsell through the slash-command item
 * that nests the SAME constraint (`/upsell_donation`). So accepting a
 * suggestion means: take the constraint the AI names, find the agent's own
 * entry point for it, and add that.
 *
 * Everything here is pure so the resolution rules can be unit-tested against
 * menu shapes without a store.
 */

/** The `suggestion_type` the AI uses for upsell offers. */
export const OFFER_UPSELL_SUGGESTION_TYPE = 'offer_upsell';

/**
 * The upsell stages whose Accept adds to the cart. Only the post-readback
 * donation for now (HITL-1429) — the mid-order and end-of-order stages stay
 * TTS-only until their carrier items become agent-reachable (MOT-17745 /
 * MOT-17770). Widening is an entry in this set, not a rewrite.
 */
export const CART_ADDING_UPSELL_VERDICTS: ReadonlySet<string> = new Set([
  SuggestionVerdict.postReadbackUpsell,
]);

/**
 * Is this suggestion one whose Accept should also add an item to the cart?
 *
 * Deliberately keyed off the self-describing payload — the `suggestion_type`,
 * the stage `verdict`, and the presence of a constraint to resolve — and NOT
 * off `reason_code`. `reason_code` was the discriminator both earlier attempts
 * at this ticket gated on, and it has since been dropped from the event
 * entirely; the effect was an accept handler that early-returned and silently
 * added nothing, which is the exact bug this ticket exists to fix. Anything
 * that fails this check falls through to today's speak-only behaviour.
 */
export const isCartAddingUpsellSuggestion = (
  suggestion: IAISuggestion
): boolean =>
  suggestion.suggestion_type === OFFER_UPSELL_SUGGESTION_TYPE &&
  CART_ADDING_UPSELL_VERDICTS.has(suggestion.verdict) &&
  Boolean(suggestion.metadata?.upsell?.constraint_name);

/**
 * The agent's entry point for the upsell the AI named, or `undefined` when the
 * menu has none.
 *
 * Resolution is by CONSTRAINT, never by name pattern: one Dairy Queen brand
 * menu carries 20 charity-specific variants of the same donation upsell
 * (`round_up_donation_gigi`, `round_up_donation_childrens_health`, …), each
 * gated by `restaurant_groups` so exactly one surfaces per restaurant. A
 * hardcoded name would break per lane and a `round_up_donation` prefix match
 * would cheerfully resolve the WRONG charity.
 *
 * `item_name` is consulted first but almost never wins: it names the AI's
 * internal container, which is filtered out below for not being `/`-prefixed.
 * It is honoured only where the AI happens to name a slash item directly.
 *
 * Returning `undefined` is a normal outcome, not an error — plenty of
 * AI-fireable upsell constraints have no slash-command carrier at all (in the
 * verified qa_3 menu: all 20 donation constraints resolve, but only 3 of the
 * other 54 do). Callers must degrade to speaking the line and adding nothing.
 */
export const resolveUpsellSlashItem = ({
  topLevelMenuItems,
  brandMenuItemsByName,
  upsell,
}: {
  topLevelMenuItems: GenericMap<TopLevelMenuItem>;
  brandMenuItemsByName: Record<string, IMUItem>;
  upsell: IUpsellSuggestionDetails | undefined;
}): TopLevelMenuItem | undefined => {
  if (!upsell) {
    return undefined;
  }
  // Agent-reachable candidates only: active, and a slash command (the item the
  // agent can actually put in the cart). `available` is deliberately not
  // checked — the omnibar autoprompt path this mirrors filters on `inactive`
  // alone.
  const slashItems = Object.values(topLevelMenuItems).filter(
    (item) => !item.inactive && item.name?.startsWith('/')
  );

  const { item_name: itemName, constraint_name: constraintName } = upsell;

  if (itemName) {
    const namedItem = slashItems.find(
      (item) => item.name === itemName || item.id === itemName
    );
    if (namedItem) {
      return namedItem;
    }
  }

  if (!constraintName) {
    return undefined;
  }
  // `id` is the full brand-menu item name, which is how brandMenuItemsByName is
  // keyed; `name` is only the intent half (`/upsell_donation` out of
  // `/upsell_donation:Would you like to round up?`).
  return slashItems.find((item) =>
    brandMenuItemsByName[item.id]?.constraints?.includes(constraintName)
  );
};

/**
 * The queue intent for a resolved item, matching how useSlashCommandReminder
 * seeds it: the item name up to the first `:` (unified-menu names carry no
 * phrase suffix, so this is a no-op there).
 */
export const getSlashCommandIntent = (item: TopLevelMenuItem): string =>
  (item.name ?? '').split(':')[0];
